import * as React from "react";
import Modal from "../../Modal/Modal";
import Input from "../../Input-new/Input";
import { getData } from "../../../requests/CRUD";
import { FIRMS } from "../../../consts/tables";
import { downloadDocumentWord } from "../../../requests/repAgreement";
import { isSummitFirm } from "./PsaForm";

/**
 * Commission Agreement form + generator (Buyside Agent tab).
 *
 * The "Commission Agreement" button opens a data-entry modal pre-filled from
 * the property record, modelled on the LOI form on the Listing Agent tab and
 * the Peak Agreement form on the Loan Info tab. Everything that reaches the
 * document is shown in the modal first, so the values can be confirmed - or
 * corrected - before anything is generated.
 *
 * Two outputs, as everywhere else in the app: Generate opens the print dialog
 * (Save as PDF), and Word posts the same document to the backend converter
 * (App\Services\Documents\HtmlToDocx) for a real .docx. Both carry the same
 * name - see documentTitleFor.
 *
 * The document itself is the firm's standard Commission Agreement. Its clause
 * text is reproduced verbatim below; only the five merged values - cooperating
 * broker, property, commission, date, and the cooperating broker's name on the
 * signature block - ever change. The Summit signatory is fixed at Jason
 * Vitorino and is deliberately not editable.
 */

// The Summit signatory. Fixed on every agreement, hence a constant rather than
// a form field.
const SUMMIT_SIGNATORY = 'Jason Vitorino';
const SUMMIT_SIGNATORY_TITLE = 'Managing Partner';

// The body of the template is set in Times New Roman 11pt; the converter's own
// default is 10.5, so the size travels with the request.
const WORD_FONT_SIZE = 11;

const escapeHtml = (value) =>
    String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

// --- Commission -------------------------------------------------------------

const ONES = [
    'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const wholeToWords = (n) => {
    if (n < 20) return ONES[n];
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    return TENS[tens] + (ones ? `-${ONES[ones]}` : '');
};

// The hundredths of a percent, spelled the way the agreement spells them: the
// three commission fractions that actually occur get their common names, and
// anything else falls back to tenths or hundredths so an unusual split still
// reads as English rather than as a decimal.
const fractionToWords = (hundredths) => {
    if (hundredths === 25) return 'one quarter';
    if (hundredths === 50) return 'one half';
    if (hundredths === 75) return 'three quarters';
    if (hundredths % 10 === 0) {
        const tenths = hundredths / 10;
        return `${wholeToWords(tenths)} ${tenths === 1 ? 'tenth' : 'tenths'}`;
    }
    return `${wholeToWords(hundredths)} hundredths`;
};

/**
 * The commission written out as the agreement writes it, e.g.
 *
 *   1.5  -> "one and one half percent (1.5%)"
 *   2    -> "two percent (2%)"
 *   0.5  -> "one half percent (0.5%)"
 *
 * A commission of 100 or more has no business being on an agreement and is not
 * worth spelling, so it prints as a numeral alone.
 */
const commissionInWords = (value) => {
    const numeral = formatCommission(value);
    const rounded = Math.round(value * 100) / 100;
    if (!(rounded > 0) || rounded >= 100) return `${numeral}%`;

    const whole = Math.floor(rounded);
    const hundredths = Math.round((rounded - whole) * 100);

    if (whole && hundredths) return `${wholeToWords(whole)} and ${fractionToWords(hundredths)} percent (${numeral}%)`;
    if (whole) return `${wholeToWords(whole)} percent (${numeral}%)`;
    // Under one percent, where "three quarters percent" does not read as
    // English: the fraction is of one percent, and says so.
    return `${fractionToWords(hundredths)} of one percent (${numeral}%)`;
};

/**
 * The commission as a numeral: one decimal where there is one, no trailing
 * zero on a whole number, and a second decimal kept rather than rounded away
 * where the multiplication produces one - a commission stated to less
 * precision than it was calculated to is the wrong commission.
 *
 *   1.5 -> "1.5"   2 -> "2"   1.35 -> "1.35"
 */
const formatCommission = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
};

// A percentage off the record, which can arrive as "3.00000000000000", "50",
// "50%" or null. Returns null when there is no usable number.
const toPercent = (value) => {
    if (value == null || value === '') return null;
    const n = parseFloat(String(value).replace(/[%,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
};

// Fee Percentage (Rep Agreement tab) x Outside Agent % (Buyside Agent tab):
// 3.0% of the price, of which the outside agent takes 50%, is a 1.5% commission.
const commissionFrom = (feePercent, outsidePercent) => {
    if (!feePercent || !outsidePercent) return null;
    return (feePercent * outsidePercent) / 100;
};

// --- Dates ------------------------------------------------------------------

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const ordinal = (day) => {
    const tens = day % 100;
    if (tens >= 11 && tens <= 13) return `${day}th`;
    switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
    }
};

const asMdy = (date) =>
    `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

// "17th day of July 2026", as the acknowledgement line is worded. An entry the
// browser cannot read falls back to today rather than printing a broken date.
const acknowledgedOn = (mdy) => {
    const parts = String(mdy || '').match(/^\s*(\d{1,2})\s*[/-]\s*(\d{1,2})\s*[/-]\s*(\d{2,4})\s*$/);
    let date = new Date();
    if (parts) {
        const year = Number(parts[3]) < 100 ? 2000 + Number(parts[3]) : Number(parts[3]);
        const parsed = new Date(year, Number(parts[1]) - 1, Number(parts[2]));
        if (!Number.isNaN(parsed.getTime())) date = parsed;
    }
    return `${ordinal(date.getDate())} day of ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

// --- Property ---------------------------------------------------------------

const propertyAddressOf = (data = {}) =>
    [data.ADDRESS, [data.CITY, data.STATE].filter(Boolean).join(', '), data.ZIP]
        .filter(Boolean)
        .join(' ');

/**
 * What the document is called, and so what it saves as from both the print
 * dialog and the Word download:
 *
 *   Commission Agreement - Cedar Point Plaza - Austin TX
 *
 * Unlike the Rep and Peak agreements this carries no date: the agreement is
 * named for the deal, and the acknowledgement line inside it carries the day.
 */
const documentTitleFor = (data = {}) =>
    sanitizeFilename(
        ['Commission Agreement', data.NAME, [data.CITY, data.STATE].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' - ')
    );

/**
 * A name every OS will take. The backend strips the same characters from the
 * .docx it names (see RepAgreementWordRequest::filename), but the printed PDF
 * is named by document.title, which nothing else cleans - so both outputs are
 * cleaned here and stay identically named.
 */
const sanitizeFilename = (name) =>
    String(name == null ? '' : name)
        // Path separators and the characters Windows reserves, plus controls.
        .replace(/[\\/:*?"<>|\x00-\x1F]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();

// --- The agreement ----------------------------------------------------------

const BROKER_NAME = 'Summit RE';

const COMMISSION_CLAUSE_POST =
    ' of the Purchase Price, provided that Cooperating Broker procures the Buyer, Broker and Cooperating ' +
    'Broker are successful in negotiating and consummating an Agreement of Purchase and Sale, the Property ' +
    'closes and funds, and Broker’s fee is paid by Seller.';

const TERM_CLAUSE =
    'This Agreement shall remain in effect for as long as the listing agreement between Broker and the Seller.';

// The rules a signature line is written on, typed in underscores. The
// converter sets a typed rule at 7pt (HtmlToDocx::RULE_HALF_POINTS), where a
// Times New Roman underscore advances half an em.
const SIG_RULE = '_'.repeat(60);
const DATE_RULE = '_'.repeat(24);

// The printed form's own stylesheet. The Word file is laid out by the backend
// converter from the same class names instead, so the two are kept in step by
// the classes rather than by these rules.
const STYLE_BLOCK = `
    @page { size: 8.5in 11in portrait; margin: 1in 1in 0.6in 1in; }
    * { box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 11pt; line-height: 1.15; margin: 0; }
    h1 { text-align: center; font-size: 11pt; font-weight: bold; margin: 0 0 36px; }
    p { text-align: justify; margin: 0 0 14px; }
    p.center { text-align: center; }
    /* The party a block is signed by. Not bold: the reference sets both party
       lines in plain body text. */
    .sig-party { text-align: left; margin: 0 0 30px; }
    .sig-name { margin: 0; }
    .sig-block { margin: 0 0 30px; }
    /* One line of a signature block. The label sits in a column of its own, so
       the rules start clear of the party line above them and "By:" and "Name:"
       - different widths - still begin in the same place. Word gets the same
       from rule-row's own tab stop. */
    /* Set in from the margin, with the rule column carried out to match, so
       the lines a party signs on sit right of the party line above them.
       Matches rule-row-indent in HtmlToDocx, in the px it counts in: an 80px
       left indent with the first line pulled back 32px, so the label starts
       at 48px - half an inch in from the margin - and the rule two spaces
       past "By:". The negative text-indent is what makes the signatory
       printed under the rule line up with the rule rather than with the
       "By:" beside it. */
    .rule-row-indent { margin: 0 0 14px; padding-left: 80px; text-indent: -32px; }
    /* Typed in underscores rather than drawn, at the weight Word rules it. */
    .rule-fill { font-size: 7pt; font-weight: normal; }
`;

const NOTE_ERROR = { marginTop: '12px', maxWidth: '880px', color: '#d0021b', fontSize: '13px' };
const NOTE_MUTED = { marginTop: '12px', maxWidth: '880px', color: '#666', fontSize: '13px' };

const CommissionAgreementForm = ({ data = {}, deleted = false, disabled = false }) => {
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(() => emptyForm(data));

    // The two percentages the commission is worked out from, read straight off
    // the record so the modal can say which one is missing.
    const feePercent = toPercent(data.SELLER_FEE_PERCENTAGE);
    const outsidePercent = toPercent(data.BUYER_AGENT_4_PERCENT);

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const reset = () => {
        setForm(emptyForm(data));
        setOpen(false);
    };

    // Open with fresh values, then fill the Cooperating Broker from the Buyer
    // Agent Firm once the lookup returns - BUYER_AGENT_4_FIRM is an id on the
    // record, and the document needs the firm's name. A value already typed is
    // left alone.
    //
    // Despite its name that column is the whole buy side's brokerage, not an
    // outside agent's, and the SYNC FIRM buttons on this tab write Summit's own
    // firm into it - so on a deal Summit bought as well as listed it holds
    // SUMMIT. There is no cooperating broker to name on such a deal, and an
    // agreement between Summit RE and Summit RE is not one, so the name is
    // withheld and the modal says why rather than pre-filling it.
    const openForm = () => {
        setForm(emptyForm(data));
        setOpen(true);
        resolveFirmName(data.BUYER_AGENT_4_FIRM).then((name) => {
            if (!name) return;
            setForm((prev) => (
                prev.cooperatingBroker
                    ? prev
                    : isSummitFirm(name)
                        ? { ...prev, buysideFirmIsSummit: true }
                        : { ...prev, cooperatingBroker: name }
            ));
        });
    };

    const commissionValue = toPercent(form.commission);
    const commissionReady = commissionValue != null && commissionValue > 0;
    // The cooperating broker is named in three places in the agreement,
    // including the block it is signed on, so it is never left to a blank rule.
    const brokerReady = String(form.cooperatingBroker || '').trim() !== '';
    const ready = commissionReady && brokerReady;

    // Named so the message can say which field to go and fill in, rather than
    // rendering a 0% commission that reads as a real one.
    const missingSources = [];
    if (!feePercent) missingSources.push('Fee Percentage (Rep Agreement tab)');
    if (!outsidePercent) missingSources.push('Outside Agent % (Buyside Agent tab)');

    // Shared by both outputs - print and Word - so the two always carry the
    // same terms and the same name.
    const buildDocument = () => {
        const title = documentTitleFor(data);
        const broker = String(form.cooperatingBroker).trim();
        const property = [form.propertyName, form.propertyAddress].filter(Boolean).join(' – ');

        const p = (html) => `<p>${html}</p>`;

        // One line a party signs on, set in from the margin. The rule follows
        // a couple of spaces after the label rather than out at a shared stop,
        // so it starts just clear of "By:" - which is why the "By:" and
        // "Name:" rules begin in different places, as the reference has them.
        const signRow = (label, rule, trailing = '') =>
            `<div class="rule-row-indent">`
            + `${label}&nbsp;&nbsp;`
            + `<span class="rule-fill">${rule}</span>${trailing}`
            + `</div>`;

        // The gap before "Date:" is written as spaces so it carries into both
        // outputs; the converter has no second tab stop on this row.
        const DATE_GAP = '&nbsp;'.repeat(10);

        // The signatory is carried on the same line as the rule it signs, so
        // it moves in from the margin with it rather than sitting back under
        // the party line.
        const byAndDate = (trailing = '') =>
            signRow('By:', SIG_RULE, `${DATE_GAP}Date: <span class="rule-fill">${DATE_RULE}</span>${trailing}`);

        const body = `
    <h1 style="margin-bottom:36px;">COMMISSION AGREEMENT</h1>

    ${p(`This COMMISSION AGREEMENT is entered into between the ${escapeHtml(BROKER_NAME)} as “Broker” and ${escapeHtml(broker)} “Cooperating Broker”.`)}

    <p class="center" style="margin-bottom:20px;">WITNESSETH</p>

    ${p(`<strong>Property:</strong> ${escapeHtml(property)}`)}

    ${p(`Broker hereby agrees to pay Cooperating Broker a commission equal to ${escapeHtml(commissionInWords(commissionValue))}${escapeHtml(COMMISSION_CLAUSE_POST)}`)}

    ${p(escapeHtml(TERM_CLAUSE))}

    <p style="margin-top:24px;margin-bottom:30px;">ACKNOWLEDGED AND ACCEPTED on this ${escapeHtml(acknowledgedOn(form.date))}</p>

    <div class="sig-block">
        <p class="sig-party" style="margin-bottom:30px;">BROKER: ${escapeHtml(BROKER_NAME.toUpperCase())}</p>
        ${byAndDate(`<br/>${escapeHtml(SUMMIT_SIGNATORY)}<br/>${escapeHtml(SUMMIT_SIGNATORY_TITLE)}`)}
    </div>

    <div class="sig-block">
        <p class="sig-party" style="margin-top:24px;margin-bottom:30px;">COOPERATING BROKER: ${escapeHtml(broker)}</p>
        ${byAndDate()}
        ${signRow('Name:', SIG_RULE)}
    </div>
`;

        return {
            title,
            html: `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${STYLE_BLOCK}</style>
</head>
<body>
${body}
</body>
</html>`,
            wordHtml: body,
        };
    };

    const generate = () => {
        if (!ready) return;
        const { html, title } = buildDocument();

        // Print via a hidden iframe so the dialog opens in place - no new tab, no
        // redirect, and the app page itself is never sent to the printer.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

        // Chrome names the saved PDF after the browser tab rather than the frame
        // it is printing, so the tab wears the document's name - the same name
        // the Word download uses - until the dialog closes.
        const previousTitle = document.title;
        const cleanup = () => {
            document.title = previousTitle;
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        iframe.onload = () => {
            const cw = iframe.contentWindow;
            cw.focus();
            cw.onafterprint = cleanup;
            cw.print();
            // Fallback cleanup in case onafterprint never fires (e.g. dialog cancelled).
            setTimeout(cleanup, 60000);
        };

        document.title = title;
        iframe.srcdoc = html;
        document.body.appendChild(iframe);

        reset();
    };

    const downloadWord = async () => {
        if (!ready) return;
        const { wordHtml, title } = buildDocument();
        await downloadDocumentWord(wordHtml, title, WORD_FONT_SIZE);
        reset();
    };

    return (
        <React.Fragment>
            <div className="Buyside-agent_column_item" style={{ minHeight: 0, margin: 0, marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <button
                    type="button"
                    className="Buyside-agent_column_item_loi button-new text"
                    disabled={disabled || deleted}
                    onClick={(e) => { e.preventDefault(); openForm(); }}
                >
                    Commission Agreement
                </button>
            </div>

            <Modal className="loi-main" show={open} onClose={reset}>
                <div className="loi-modal">
                    <span className="green-title"> Commission Agreement </span>
                    <div className="close-button" onClick={reset} />
                </div>
                <div
                    className="loi-modal-content"
                    style={{ display: 'grid', gap: '4px 80px', gridTemplateColumns: '400px 400px' }}
                >
                    <Input
                        label="Commission (%)"
                        value={form.commission}
                        onChange={(event) => update('commission', event.target.value)}
                    />
                    <Input
                        label="Date"
                        value={form.date}
                        onChange={(event) => update('date', event.target.value)}
                    />
                    <Input
                        label="Cooperating Broker"
                        value={form.cooperatingBroker}
                        onChange={(event) => update('cooperatingBroker', event.target.value)}
                    />
                    <Input
                        label="Summit Signatory"
                        value={SUMMIT_SIGNATORY}
                        onChange={() => {}}
                        disabled
                    />
                    <Input
                        label="Property Name"
                        value={form.propertyName}
                        onChange={(event) => update('propertyName', event.target.value)}
                    />
                    <Input
                        label="Property Address"
                        value={form.propertyAddress}
                        onChange={(event) => update('propertyAddress', event.target.value)}
                    />
                </div>

                {
                    // A 0% commission printed on an agreement is a real error, so
                    // the missing field is named and generation is held until it
                    // is filled in - here or on the tab it belongs to.
                    !commissionReady && missingSources.length > 0 &&
                    <div style={NOTE_ERROR}>
                        {`Commission cannot be calculated: ${missingSources.join(' and ')} ${missingSources.length > 1 ? 'are' : 'is'} missing or zero. Fill it in on the property, or enter the commission above, to generate the agreement.`}
                    </div>
                }
                {
                    !commissionReady && missingSources.length === 0 &&
                    <div style={NOTE_ERROR}>
                        Enter a commission greater than zero to generate the agreement.
                    </div>
                }
                {
                    // Summit on the buy side as well as the listing: no third
                    // party to pay, so the name is asked for rather than guessed.
                    !brokerReady && form.buysideFirmIsSummit &&
                    <div style={NOTE_ERROR}>
                        Buyer Agent Firm is Summit's own brokerage, so there is no cooperating broker on this deal. Enter the cooperating broker's firm above to generate the agreement.
                    </div>
                }
                {
                    !brokerReady && !form.buysideFirmIsSummit &&
                    <div style={NOTE_ERROR}>
                        Enter the Cooperating Broker to generate the agreement. It is named in the opening paragraph and on the signature block.
                    </div>
                }
                {
                    commissionReady &&
                    <div style={NOTE_MUTED}>
                        {`Commission will read: ${commissionInWords(commissionValue)}`}
                    </div>
                }

                <div className="modal-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '16px' }}>
                    <button type="button" className="button-new positive" disabled={!ready} onClick={generate}>Generate</button>
                    <button type="button" className="button-new negative" disabled={!ready} onClick={downloadWord}>Word</button>
                    <button type="button" className="button-new negative" onClick={reset}>Cancel</button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

/**
 * Everything the modal opens with, read off the property record. Each stays
 * editable: the record is the starting point, not the last word.
 */
function emptyForm(data = {}) {
    const commission = commissionFrom(
        toPercent(data.SELLER_FEE_PERCENTAGE),
        toPercent(data.BUYER_AGENT_4_PERCENT)
    );

    return {
        commission: commission == null ? '' : formatCommission(commission),
        date: asMdy(new Date()),
        cooperatingBroker: '',
        buysideFirmIsSummit: false,
        propertyName: data.NAME || '',
        propertyAddress: propertyAddressOf(data),
    };
}

/**
 * The Buyer Agent Firm's name. BUYER_AGENT_4_FIRM holds the firm's id - the
 * same lookup SelectFirm makes to show the name on the tab. Returns '' when
 * there is no firm on the record or it cannot be read.
 */
async function resolveFirmName(firmId) {
    if (!firmId) return '';
    try {
        const res = await getData({
            tableCode: FIRMS,
            options: {
                column: ['ARCIMEDES_ROW_ID', 'NAME'],
                limit: { count: 1, offset: 0 },
                filter: [{ code: 'ARCIMEDES_ROW_ID', text: String(firmId), type: 'equal' }],
                filterType: 'and',
            },
        });
        const row = res && res.success && Array.isArray(res.data) && res.data[0];
        return row && row.NAME ? row.NAME : '';
    } catch (e) {
        return '';
    }
}

export default CommissionAgreementForm;
